import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { doc, setDoc, updateDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const UserProfile = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});

  // Default avatar for all users
  const defaultAvatar = "https://i.fbcd.co/products/resized/resized-750-500/d4c961732ba6ec52c0bbde63c9cb9e5dd6593826ee788080599f68920224e27d.jpg";

  // Fetch or create user profile in Firestore
  useEffect(() => {
    if (!currentUser) return;

    const userRef = doc(db, "users", currentUser.uid);
    const unsubscribe = onSnapshot(userRef, async (docSnap) => {
      if (docSnap.exists()) {
        setUser(docSnap.data());
        setFormData(docSnap.data());
      } else {
        const defaultUserData = {
          name: currentUser.displayName || "Jane Doe",
          email: currentUser.email,
          avatar: defaultAvatar, // Set default avatar for all users
          phone: "",
          bio: "",
          membership: "Bronze",
          ordersCount: 0,
          recentOrders: [],
        };
        await setDoc(userRef, defaultUserData);
        setUser(defaultUserData);
        setFormData(defaultUserData);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Fetch recent orders
  useEffect(() => {
    if (!currentUser) return;

    const ordersRef = collection(db, "orders");
    const q = query(ordersRef, where("userId", "==", currentUser.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orders = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      orders.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
      setUser((prev) => ({
        ...prev,
        recentOrders: orders.slice(0, 5),
        ordersCount: orders.length,
      }));
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, { ...formData });
      setUser(formData);
      setIsEditing(false);
      alert("Profile updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to save profile.");
    }
  };

  const handleCancel = () => {
    setFormData(user);
    setIsEditing(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (err) {
      console.error("Logout failed:", err);
      alert("Failed to logout.");
    }
  };

  if (!user)
    return (
      <div className="min-h-screen flex items-center justify-center">Loading...</div>
    );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-indigo-200 via-purple-100 to-pink-100 p-6">
      <div className="bg-white bg-opacity-80 backdrop-blur-md rounded-2xl shadow-xl p-8 w-full max-w-3xl space-y-8">
        {/* User Avatar */}
        <div className="flex flex-col items-center">
          <img
            src={user.avatar || defaultAvatar}
            alt={user.name || currentUser.displayName || "User"}
            className="w-32 h-32 rounded-full object-cover border-4 border-indigo-600 shadow-md"
          />
          {/* Display Name */}
          <h1 className="text-3xl font-bold text-center mt-4">
            {user.name || currentUser.displayName || "User Name"}
          </h1>
        </div>

        {/* Profile Form / Info */}
        <div>
          {isEditing ? (
            <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
              {["name", "email", "phone", "bio"].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{field}</label>
                  {field === "bio" ? (
                    <textarea
                      name="bio"
                      rows={3}
                      value={formData.bio}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  ) : (
                    <input
                      type={field === "email" ? "email" : "text"}
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  )}
                </div>
              ))}

              <div className="flex justify-end gap-3">
                <button type="button" onClick={handleCancel} className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition">Cancel</button>
                <button type="submit" onClick={handleSave} className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition">Save</button>
              </div>
            </form>
          ) : (
            <>
              <p className="text-gray-600">Email: {user.email}</p>
              <p className="text-gray-600">Phone: {user.phone || "Phone not set"}</p>
              {user.bio && <p className="text-gray-700 italic mt-2">Bio: {user.bio}</p>}

              <div className="mt-4 flex flex-wrap gap-4 items-center">
                <span className="inline-block bg-yellow-300 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium shadow">{user.membership} Member</span>
                <span className="inline-block bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium shadow">{user.ordersCount} Orders</span>
              </div>

              <div className="flex gap-4 mt-6">
                <button onClick={() => setIsEditing(true)} className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">Edit Profile</button>
                <button onClick={handleLogout} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition">Logout</button>
              </div>
            </>
          )}
        </div>

        {/* Recent Orders */}
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Recent Orders</h3>
          <ul className="space-y-2">
            {user.recentOrders && user.recentOrders.length > 0 ? (
              user.recentOrders.map((order) => (
                <li key={order.id} className="flex justify-between items-center bg-white rounded-lg px-4 py-2 border shadow-sm">
                  <span className="text-gray-700">{order.cartItems?.map((i) => i.name).join(", ") || "No items"}</span>
                  <span className="text-sm text-gray-500">{order.createdAt?.toDate?.()?.toLocaleDateString() || "N/A"}</span>
                </li>
              ))
            ) : (
              <li className="flex justify-between items-center bg-white rounded-lg px-4 py-2 border shadow-sm text-gray-500">No recent orders.</li>
            )}
          </ul>
        </div>

        <div className="flex justify-center pt-4">
          <Link to="/" className="inline-block bg-pink-600 hover:bg-pink-700 text-white text-sm font-semibold py-2 px-6 rounded-full transition">Continue Shopping 🛍️</Link>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
